/**
 * Phase 8 T-24 §28.C — Column visibility / view dropdown.
 *
 * 240 px dropdown from the [View] button in DataTableToolbar.
 * Rows: drag handle + eye-icon toggle + column label.
 * Footer: "View style" label + [table] (active) + [gallery] (disabled / coming soon).
 *
 * Column visibility + order is persisted in `localStorage.ggj.tableView.<tableId>`.
 *
 * Drag-and-drop reorder is UI-only in Phase 8 (no backend).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Eye,
  EyeOff,
  GripVertical,
  LayoutGrid,
  Table2,
  ChevronDown,
} from 'lucide-react';

export interface ViewColumn {
  id: string;
  label: string;
  /** When false the column is hidden. Default true. */
  visible?: boolean;
}

export interface TableViewMenuProps {
  /** Stable identifier for localStorage key, e.g. "candidates", "applications". */
  tableId: string;
  columns: ViewColumn[];
  /** Called whenever visibility or order changes. */
  onChange: (columns: ViewColumn[]) => void;
}

const LS_PREFIX = 'ggj.tableView.';

function loadPrefs(tableId: string, defaults: ViewColumn[]): ViewColumn[] {
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + tableId);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as { id: string; visible: boolean }[];
    if (!Array.isArray(saved)) return defaults;
    // Merge saved prefs with defaults — add any new columns at the end.
    const savedMap = new Map(saved.map((s) => [s.id, s.visible]));
    const merged: ViewColumn[] = saved
      .map((s) => {
        const def = defaults.find((d) => d.id === s.id);
        if (!def) return null;
        return { ...def, visible: s.visible };
      })
      .filter(Boolean) as ViewColumn[];
    const seenIds = new Set(merged.map((c) => c.id));
    for (const d of defaults) {
      if (!seenIds.has(d.id)) merged.push({ ...d, visible: savedMap.get(d.id) ?? (d.visible ?? true) });
    }
    return merged;
  } catch {
    return defaults;
  }
}

function savePrefs(tableId: string, cols: ViewColumn[]): void {
  try {
    window.localStorage.setItem(
      LS_PREFIX + tableId,
      JSON.stringify(cols.map((c) => ({ id: c.id, visible: c.visible ?? true }))),
    );
  } catch {
    /* quota / private browsing */
  }
}

/** Hook that manages column state with localStorage persistence. */
export function useTableViewColumns(
  tableId: string,
  defaultColumns: ViewColumn[],
): [ViewColumn[], React.Dispatch<React.SetStateAction<ViewColumn[]>>] {
  const [cols, setCols] = useState<ViewColumn[]>(() =>
    loadPrefs(tableId, defaultColumns.map((c) => ({ ...c, visible: c.visible ?? true }))),
  );

  // Persist on every change.
  useEffect(() => {
    savePrefs(tableId, cols);
  }, [tableId, cols]);

  return [cols, setCols];
}

export function TableViewMenu({ tableId, columns, onChange }: TableViewMenuProps) {
  const [open, setOpen] = useState(false);
  const [localCols, setLocalCols] = useState<ViewColumn[]>(
    columns.map((c) => ({ ...c, visible: c.visible ?? true })),
  );

  // Sync if parent columns change (e.g. on mount with localStorage-loaded values).
  const prevColumns = useRef(columns);
  useEffect(() => {
    if (columns !== prevColumns.current) {
      prevColumns.current = columns;
      setLocalCols(columns.map((c) => ({ ...c, visible: c.visible ?? true })));
    }
  }, [columns]);

  const toggleVisible = useCallback(
    (id: string) => {
      setLocalCols((prev) => {
        const next = prev.map((c) => (c.id === id ? { ...c, visible: !c.visible } : c));
        onChange(next);
        savePrefs(tableId, next);
        return next;
      });
    },
    [tableId, onChange],
  );

  // Drag state — simplified; just tracks which item is dragging and where.
  const dragIdx = useRef<number | null>(null);

  const onDragStart = (idx: number) => {
    dragIdx.current = idx;
  };

  const onDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    const from = dragIdx.current;
    if (from == null || from === idx) return;
    setLocalCols((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved!);
      dragIdx.current = idx;
      onChange(next);
      savePrefs(tableId, next);
      return next;
    });
  };

  const onDragEnd = () => {
    dragIdx.current = null;
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-3 text-[13px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
        style={{
          borderColor: 'var(--vscode-border-primary)',
          color: 'var(--vscode-text-secondary)',
        }}
      >
        <Eye className="h-3.5 w-3.5" aria-hidden />
        View
        <ChevronDown className="h-3 w-3 opacity-60" aria-hidden />
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-[180]" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute left-0 top-full z-[190] mt-1 w-[240px] rounded-[10px] border shadow-lg"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
            }}
            role="listbox"
            aria-label="Column visibility"
          >
            {/* Column rows */}
            <div className="py-1">
              {localCols.map((col, idx) => (
                <div
                  key={col.id}
                  draggable
                  onDragStart={() => onDragStart(idx)}
                  onDragOver={(e) => onDragOver(e, idx)}
                  onDragEnd={onDragEnd}
                  className="flex cursor-grab items-center gap-2 px-3 py-1.5 hover:bg-[var(--vscode-bg-hover)]"
                  role="option"
                  aria-selected={col.visible ?? true}
                >
                  <GripVertical
                    className="h-3.5 w-3.5 shrink-0 opacity-30"
                    aria-hidden
                  />
                  <button
                    type="button"
                    onClick={() => toggleVisible(col.id)}
                    aria-label={`${(col.visible ?? true) ? 'Hide' : 'Show'} ${col.label}`}
                    className="shrink-0 rounded p-0.5 transition-colors hover:bg-[var(--vscode-bg-hover)]"
                    style={{ color: (col.visible ?? true) ? '#10B981' : 'var(--vscode-text-tertiary)' }}
                  >
                    {(col.visible ?? true) ? (
                      <Eye className="h-3.5 w-3.5" aria-hidden />
                    ) : (
                      <EyeOff className="h-3.5 w-3.5" aria-hidden />
                    )}
                  </button>
                  <span
                    className="flex-1 truncate text-[13px]"
                    style={{ color: 'var(--vscode-text-secondary)' }}
                  >
                    {col.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer: view style */}
            <div
              className="flex items-center justify-between border-t px-3 py-2"
              style={{ borderColor: 'var(--vscode-border-primary)' }}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--vscode-text-tertiary)' }}>
                View style
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  aria-label="Table view (active)"
                  className="rounded-[6px] p-1.5 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                  title="Table view"
                >
                  <Table2 className="h-3.5 w-3.5" aria-hidden />
                </button>
                <button
                  type="button"
                  disabled
                  aria-label="Gallery view (coming soon)"
                  title="Coming soon"
                  className="cursor-not-allowed rounded-[6px] p-1.5 opacity-35"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                >
                  <LayoutGrid className="h-3.5 w-3.5" aria-hidden />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
